import type { Card } from '@/types/entities'

/**
 * Make a work item safe to render, whatever the API handed us.
 *
 * The board reads a work item's lists directly — `assigneeIds.map(...)`, `labelIds.map(...)`.
 * That is correct against the schema, where every one of those columns is NOT NULL. It is not
 * correct against reality: a row written before a column existed, or by a code path that
 * omitted it, comes back with `null`, and one such row took down the entire Work tab with
 * "Cannot read properties of null (reading 'map')".
 *
 * A single malformed row should cost you that row's detail, not the page. So the lists are
 * coerced once, here, at the edge — rather than sprinkling `?? []` across every component that
 * touches a card and forgetting one.
 *
 * This is a seatbelt, not a fix. The fix is that nothing writes an incomplete row; see the
 * accept-request path in the backend, which used to.
 */
const list = <T,>(value: T[] | null | undefined): T[] => (Array.isArray(value) ? value : [])

export function normalizeCard(card: Card): Card {
  const assigneeIds = [...new Set([card.ownerId, ...list(card.assigneeIds)].filter((id): id is string => Boolean(id)))]
  return {
    ...card,
    // Historical single-assignee rows sometimes have only ownerId. Treat that person as both
    // assigned and the owner so the multi-assignee UI is backward-compatible.
    assigneeIds,
    approverIds: list(card.approverIds),
    approvals: list(card.approvals),
    labelIds: list(card.labelIds),
    teamPathIds: list(card.teamPathIds),
    watcherIds: list(card.watcherIds),
    customAttrs: card.customAttrs ?? {},
  }
}

export const normalizeCards = (cards: Card[] | null | undefined): Card[] =>
  list(cards).map(normalizeCard)
